// Choropleth of early-voting and mail-voting access, one shape per state and
// DC, with a timeline of election years above it. Selecting a year recolors the
// map, and hovering or tapping a state names it and its category.
//
// The outlines arrive already projected (see scripts/build_geo.js) on a fixed
// canvas, so the map scales by viewBox and this module draws path strings.

const LEGEND_LABELS = {
  both: 'Early in-person and mail-in',
  earlyOnly: 'Early in-person, restricted mail-in',
  neither: 'No early in-person, restricted mail-in',
};

const LEGEND_ORDER = ['both', 'earlyOnly', 'neither'];

// Top to bottom inside each timeline column, worst access on top
const STACK_ORDER = ['neither', 'earlyOnly', 'both'];

const NOTE_LABELS = {
  '*': 'temporary pandemic-era expansion',
  '†': 'all-mail election',
};

const COMPACT_BELOW = 720;

// Below this the legend's three columns get too narrow to read and it stacks.
// Measured on the graphic, which runs narrower than the viewport once the
// article column's padding is taken out.
const LEGEND_STACKED_BELOW = 330;

// Axis-label size from the style guide
const TICK_LABEL_SIZE = 12;

// Where the things that are pinned to screen pixels, rather than scaling with
// the map, have to step down: below this the whole country is only a few
// hundred pixels across, and a state stops being big enough to carry them --
// New Jersey is about 7px wide at 320px. Deliberately not COMPACT_BELOW, which
// is about the timeline and legend running out of room, and lands much higher.
const MAP_NARROW_BELOW = 500;

// State borders, in screen pixels
const BORDER_PX = 1.5;
const BORDER_PX_NARROW = 1;

// Callout square, in canvas units. Left to scale with the viewBox so the column
// keeps its spacing at every width; the pitch it stacks on is set in build_geo.
const CALLOUT_SIZE = 30;

// Between a callout square and its label, in canvas units
const CALLOUT_LABEL_GAP = 7;

// Sizes held at a constant number of screen pixels, whatever the graphic's
// width, by converting through the canvas-units-per-pixel scale. Left in canvas
// units they would shrink with the map, and a phone would get 4px labels.
const CALLOUT_LABEL_PX = 11;

// One tiled envelope. Smaller on a narrow map, where an 18px envelope is wider
// than several states and the motif stops tiling within a shape at all -- the
// point of it is a texture, which needs more than one envelope to read as one.
const ENVELOPE_PX = 18;
const ENVELOPE_PX_NARROW = 11;

// Space between tiled envelopes, at ENVELOPE_PX. Drawn edge to edge the motif
// reads as a mesh rather than a run of envelopes. Scales with the envelope, so
// the gutter stays the same share of the tile at either size.
const ENVELOPE_GAP_PX = 4;

// The envelope, in a 24-unit box: the flap folded down from the top edge, with
// a rounded tip, plus the body seams climbing in from the bottom corners and
// stopping where they meet the sides of the flap rather than its point.
const ENVELOPE_BOX = 24;
const FLAP_PATH = 'M1 1 H23 L12.95 19.74 Q12 21.5 11.05 19.74 Z';
const SEAMS_PATH = 'M1 23 L7.68 13.46 M23 23 L16.32 13.46';

const PATTERN_ID = 'state-map-envelope';

// Tooltip, in screen pixels: how far the card sits off the pointer, and how
// near the edge of the map it may come before it folds back the other way
const TOOLTIP_OFFSET = 12;
const TOOLTIP_EDGE = 4;

// Survives the redraw that pym triggers on every width change
let selectedYear = null;

// So does this: the hint has served its purpose once the reader has worked the
// timeline, and resizing the page shouldn't bring it back
let tapHintUsed = false;

function metrics(width, compact) {
  const tickLabelGap = compact ? 5 : 7;

  const narrow = width < MAP_NARROW_BELOW;

  return {
    tickLabelGap,
    borderWidth: narrow ? BORDER_PX_NARROW : BORDER_PX,
    envelope: narrow ? ENVELOPE_PX_NARROW : ENVELOPE_PX,
    barHeight: compact ? 38 : 46,
    blockGap: compact ? 14 : 30,
    tickGap: compact ? 2 : 5,
  };
}

// Two digits in the narrow layout, where a column is barely wider than one
// character of the axis type. The buttons keep the full year in their label and
// title, so this is only ever what's drawn. The mark is a closing quote rather
// than a straight apostrophe, which is what an elided century takes.
function tickLabel(year, compact) {
  return compact ? `’${String(year).slice(-2)}` : String(year);
}

// One category's share of the map, to sit under its legend label. Every state
// and DC is exactly one of the three, so the denominator is 51 and the three
// shares describe the same composition as the bar for that year. Rounded
// plainly rather than apportioned, so five of the fourteen years total 101%.
function shareLabel(count, total) {
  return `${Math.round((count / total) * 100)}%`;
}

// Names the denominator once, on the first share; the other two read as the
// same measure by sitting beside it. The marker is where DC gets accounted
// for, since it is in the count but is not a state.
const SHARE_OF = ' of states*';

function noteText(markers) {
  return markers
    .split('')
    .map(marker => NOTE_LABELS[marker])
    .filter(Boolean)
    .join('; ');
}

function el(tag, className, text) {
  const node = document.createElement(tag);

  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;

  return node;
}

function svgEl(tag, className) {
  const node = document.createElementNS('http://www.w3.org/2000/svg', tag);

  if (className) node.setAttribute('class', className);

  return node;
}

function attrs(node, values) {
  Object.keys(values).forEach(key => node.setAttribute(key, values[key]));

  return node;
}

// Enough precision for a coordinate on a 1000-unit canvas, without writing out
// seventeen digits of a division
function round(value) {
  return Math.round(value * 10) / 10;
}

// Grouped so the fade applies once to the pair. Fading them separately would
// double the alpha where a seam crosses the flap and leave a bright nick.
function envelopeInk() {
  const ink = svgEl('g', 'state-map__envelope-ink');

  ink.appendChild(attrs(svgEl('path', 'state-map__flap'), { d: FLAP_PATH }));
  ink.appendChild(attrs(svgEl('path', 'state-map__seams'), { d: SEAMS_PATH }));

  return ink;
}

// The tile the whole motif is built from: the envelope's 24-unit box centered
// in a larger one, the difference being the gap between neighbors.
function envelopeTile() {
  const pitch = ENVELOPE_PX + ENVELOPE_GAP_PX;
  const box = (ENVELOPE_BOX * pitch) / ENVELOPE_PX;
  const inset = round((box - ENVELOPE_BOX) / 2);

  return { box: round(box), inset, viewBox: `${-inset} ${-inset} ${round(box)} ${round(box)}` };
}

// Gap field, then the envelope's body over it, then the flap and seams. Drawn
// once for the legend swatch and each callout square, and repeated across the
// states as a pattern, so all three show the same texture.
//
// The bleed oversizes the gap field, which the tiling needs so neighboring
// tiles overlap rather than leaving an antialiased seam along the join.
function appendEnvelopeTile(node, bleed) {
  const { box, inset } = envelopeTile();

  node.appendChild(attrs(svgEl('rect', 'state-map__envelope-gap'), {
    x: -inset - bleed,
    y: -inset - bleed,
    width: round(box + bleed * 2),
    height: round(box + bleed * 2),
  }));

  node.appendChild(attrs(svgEl('rect', 'state-map__envelope-paper'), {
    x: 0,
    y: 0,
    width: ENVELOPE_BOX,
    height: ENVELOPE_BOX,
    rx: 1.5,
  }));

  node.appendChild(envelopeInk());
}

// Mail access carries an envelope so the category reads as more than a color.
// One tile of the tiling, at swatch or callout size.
function buildEnvelope(className, size) {
  const envelope = attrs(svgEl('svg', className), {
    viewBox: envelopeTile().viewBox,
    'aria-hidden': 'true',
    focusable: 'false',
  });

  if (size !== undefined) attrs(envelope, { width: size, height: size });

  appendEnvelopeTile(envelope, 0);

  return envelope;
}

// Across a state the motif has to tile, since one envelope stretched to Montana
// would read as a shape rather than a texture.
//
// The gap field covers the whole tile and the envelope is laid back over it,
// rather than cutting a hole for the fill underneath to show through -- a mask
// inside a pattern is a good way to lose the fill altogether on some browsers.
// The tiling only ever lands on the mail-in category, so the envelope can carry
// that color directly.
//
// The tile's own proportions are fixed, so the only thing the envelope's size
// changes is how many screen pixels one tile is rendered into -- and the gap
// comes along with it, keeping the same share of the tile at either size.
function buildPattern(unit, envelopePx) {
  const pitch = (ENVELOPE_PX + ENVELOPE_GAP_PX) * (envelopePx / ENVELOPE_PX);

  const pattern = attrs(svgEl('pattern', 'state-map__envelope-pattern'), {
    id: PATTERN_ID,
    patternUnits: 'userSpaceOnUse',
    width: round(pitch * unit),
    height: round(pitch * unit),
    viewBox: envelopeTile().viewBox,
  });

  appendEnvelopeTile(pattern, 0.5);

  return pattern;
}

// The tap affordance from the polling-president cards, so the gesture reads the
// same from graphic to graphic. A white body under a grey outline, which is what
// lets it sit legibly on the bar it points at instead of needing clear space.
const TAP_HALO = 'M4.28299 20.3287C3.99499 20.3287 3.71299 20.2277 3.50699 20.1547C1.90499 19.5847 0.278996 17.7257 0.412996 15.8657L0.424989 15.7037L0.545 15.5957C0.885667 15.2871 1.07167 15.0044 1.103 14.7477C1.117 14.6327 1.13399 14.4947 1.16199 14.3217C1.29199 13.5007 1.644 12.5647 1.984 11.6587C2.45 10.4157 2.93299 9.13172 2.83499 8.19172C2.77099 7.96472 2.7 7.71772 2.857 7.49472C2.926 7.39672 3.061 7.27672 3.314 7.26072H3.32199C3.70699 7.26072 4.094 7.41272 4.353 7.63772C5.163 8.34272 5.03399 9.34172 4.93999 10.0727C4.90999 10.3027 4.84899 10.7817 4.92499 10.8447C4.93399 10.8457 4.94299 10.8467 4.95099 10.8467C5.10099 10.8467 5.306 10.6577 5.42 10.5527L5.56 10.4267L11.029 5.70372L11.184 5.56472C11.497 5.28072 11.852 4.95972 12.331 4.95972C12.59 4.95972 12.844 5.05772 13.086 5.25072C13.519 5.62072 13.613 6.31772 13.301 6.78172C13.169 6.97872 12.969 7.14072 12.794 7.28472L9.74999 9.90872C9.75999 9.90872 9.77 9.90872 9.78 9.90872C10.188 9.90872 10.572 10.1357 10.783 10.5007C10.993 10.8657 10.994 11.2987 10.785 11.6587C10.726 11.7597 10.641 11.8587 10.535 11.9617C11.025 11.9707 11.488 12.3387 11.617 12.8237C11.788 13.4637 11.415 13.8637 11.096 14.1387C11.109 14.1387 11.123 14.1387 11.136 14.1387C11.628 14.1387 12.06 14.4347 12.211 14.8757C12.488 15.6837 11.907 16.1507 11.523 16.4607L11.352 16.6007C9.44799 18.2387 7.824 19.2897 6.525 19.7247C6.364 19.7787 6.199 19.8047 6.021 19.8047C5.915 19.8047 5.813 19.7957 5.711 19.7857C5.634 19.7787 5.55699 19.7707 5.47699 19.7707C5.38199 19.7707 5.30299 19.7827 5.22099 19.8107C5.03599 19.8807 4.83499 20.0147 4.61899 20.2167L4.51599 20.3127L4.28499 20.3277L4.28299 20.3287Z';
const TAP_HAND = 'M12.33 5.36567C12.486 5.36567 12.652 5.42366 12.834 5.56866C13.105 5.79966 13.164 6.26467 12.966 6.55967C12.837 6.75167 12.581 6.93467 12.404 7.08767L8.68199 10.3017C8.49099 10.4667 7.429 11.1577 7.877 11.4367C7.933 11.4717 7.98399 11.4857 8.03099 11.4857C8.16599 11.4857 8.27399 11.3707 8.38899 11.2717L8.98299 10.7567C9.17099 10.5927 9.354 10.3977 9.605 10.3367C9.664 10.3227 9.723 10.3157 9.78 10.3157C10.329 10.3157 10.736 10.9427 10.436 11.4607C10.317 11.6657 9.928 11.9547 9.739 12.1177L8.99799 12.7557C8.83199 12.8967 8.34499 13.2467 8.75299 13.4707C8.79499 13.4937 8.83598 13.5037 8.87498 13.5037C9.02698 13.5037 9.159 13.3577 9.269 13.2617L9.823 12.7787C9.936 12.6787 10.048 12.5677 10.171 12.4797C10.28 12.4027 10.398 12.3687 10.516 12.3687C10.833 12.3687 11.142 12.6147 11.227 12.9317C11.37 13.4677 10.946 13.7357 10.595 14.0377L9.91599 14.6217C9.73799 14.7747 9.31299 15.1247 9.71599 15.3257C9.74099 15.3387 9.768 15.3437 9.797 15.3437C10.096 15.3437 10.56 14.7047 10.875 14.5907C10.959 14.5607 11.048 14.5457 11.137 14.5457C11.435 14.5457 11.729 14.7127 11.83 15.0097C12.046 15.6407 11.5 15.9467 11.09 16.2987C9.828 17.3837 7.98399 18.8147 6.39799 19.3457C6.26299 19.3907 6.139 19.4047 6.021 19.4047C5.835 19.4047 5.66299 19.3707 5.47699 19.3707C5.35699 19.3707 5.22999 19.3847 5.09099 19.4327C4.80999 19.5387 4.56199 19.7217 4.34399 19.9257C4.32399 19.9267 4.30499 19.9277 4.28499 19.9277C4.07099 19.9277 3.838 19.8457 3.644 19.7767C2.148 19.2447 0.699986 17.5237 0.816986 15.8967C1.57699 15.2087 1.415 15.3107 1.562 14.3867C1.863 12.4887 3.42098 9.90866 3.23698 8.15266C3.21798 7.96866 3.034 7.68566 3.34 7.66666C3.354 7.66666 3.367 7.66566 3.381 7.66566C3.623 7.66566 3.913 7.79266 4.088 7.94566C5.206 8.91766 4.075 10.6757 4.67 11.1607C4.771 11.2427 4.87198 11.2767 4.97198 11.2767C5.29098 11.2767 5.595 10.9327 5.823 10.7357L11.292 6.01266C11.62 5.72866 11.943 5.36767 12.331 5.36767M12.33 4.55866C11.695 4.55866 11.242 4.96867 10.911 5.26867L10.763 5.40067L5.34399 10.0807C5.44299 9.30667 5.57 8.16566 4.616 7.33566C4.277 7.04066 3.803 6.85767 3.379 6.85767H3.353L3.286 6.86066C2.852 6.88866 2.62299 7.12566 2.52499 7.26466C2.25699 7.64566 2.374 8.05266 2.424 8.22566C2.52 9.08066 2.05499 10.3197 1.60399 11.5187C1.25699 12.4427 0.897993 13.3977 0.761993 14.2587C0.733993 14.4377 0.715988 14.5797 0.700988 14.6987C0.688988 14.7977 0.684992 14.8687 0.666992 14.9287C0.635992 14.9667 0.389988 15.1897 0.271988 15.2967L0.0319973 15.5137L0.00898705 15.8367C-0.142013 17.9457 1.63599 19.9167 3.36999 20.5337C3.59099 20.6127 3.925 20.7307 4.282 20.7307C4.323 20.7307 4.36298 20.7297 4.40298 20.7257L4.685 20.7047L4.892 20.5117C5.069 20.3467 5.22699 20.2397 5.37399 20.1837C5.38799 20.1797 5.42399 20.1737 5.47499 20.1737C5.54199 20.1737 5.606 20.1807 5.67 20.1867C5.785 20.1977 5.89999 20.2077 6.01799 20.2077C6.24099 20.2077 6.44799 20.1747 6.65099 20.1067C7.99899 19.6557 9.66799 18.5787 11.613 16.9067C11.665 16.8617 11.72 16.8177 11.774 16.7737C12.158 16.4637 12.958 15.8197 12.59 14.7447C12.462 14.3707 12.189 14.0737 11.842 13.9007C12.027 13.5997 12.135 13.2137 12.003 12.7197C11.887 12.2847 11.584 11.9257 11.199 11.7277C11.409 11.2697 11.386 10.7467 11.128 10.2997C10.99 10.0607 10.797 9.86667 10.571 9.72967L12.927 7.69566L13.046 7.59666C13.234 7.44366 13.467 7.25267 13.632 7.00667C14.059 6.36967 13.935 5.44867 13.354 4.95267C13.018 4.68367 12.679 4.55566 12.326 4.55566L12.33 4.55866Z';
// Which of the fourteen bars the hint floats over
const TAP_HINT_YEAR = 2024;

const TAP_RAYS = [
  'M10.803 3.22349L9.79999 0.854492',
  'M16.577 9.73866L14.596 8.09766',
  'M13.247 2.92557L13.755 0.403564',
  'M15.117 4.0426L17.115 2.42261',
  'M15.898 6.10449L18.404 6.68449',
];

function buildTapHint() {
  const svg = attrs(svgEl('svg', 'state-map__tap'), {
    viewBox: '0 0 19 21',
    fill: 'none',
    // The bar it sits on is a button that already announces its year
    'aria-hidden': 'true',
  });

  // Mirrored so the finger points back in over the bars instead of out at the
  // edge of the graphic. Done to the artwork rather than in CSS because the
  // float animation owns this element's own transform, and would drop the flip
  // wherever the animation is switched off.
  const flipped = attrs(svgEl('g'), { transform: 'translate(19 0) scale(-1 1)' });

  flipped.appendChild(attrs(svgEl('path', 'state-map__tap-halo'), { d: TAP_HALO }));
  flipped.appendChild(attrs(svgEl('path', 'state-map__tap-hand'), { d: TAP_HAND }));
  TAP_RAYS.forEach((d) => {
    flipped.appendChild(attrs(svgEl('path', 'state-map__tap-ray'), { d }));
  });

  svg.appendChild(flipped);

  return svg;
}

function buildTimeline(years, countsByYear, compact) {
  const timeline = el('div', 'state-map__timeline');

  const ticks = el('div', 'state-map__ticks');
  ticks.setAttribute('role', 'group');
  ticks.setAttribute('aria-label', 'Election year');

  const buttons = years.map((year, i) => {
    const button = el('button', 'state-map__tick');
    button.type = 'button';
    button.setAttribute('aria-label', String(year));
    button.title = String(year);

    const bar = el('span', 'state-map__bar');
    STACK_ORDER.forEach((key) => {
      const segment = el('span', 'state-map__segment');
      segment.dataset.category = key;
      segment.style.flexGrow = countsByYear[i][key];
      bar.appendChild(segment);
    });

    const label = el('span', 'state-map__tick-label', tickLabel(year, compact));
    label.setAttribute('aria-hidden', 'true');

    button.appendChild(bar);
    button.appendChild(label);
    ticks.appendChild(button);

    return button;
  });

  // Off the end of the row, where the icon has a gap to hang over on both sides
  // and is not covering the year the map opens on
  const hinted = years.indexOf(TAP_HINT_YEAR);
  const hint = tapHintUsed || hinted === -1 ? null : buildTapHint();
  if (hint) buttons[hinted].appendChild(hint);

  timeline.appendChild(ticks);

  return {
    timeline, ticks, buttons, hint,
  };
}

function wireTimeline(ticks, buttons, years, onSelect) {
  buttons.forEach((button, i) => {
    button.addEventListener('click', () => onSelect(years[i]));
  });

  // Arrow keys walk the timeline, so it works without 14 separate tab stops
  ticks.addEventListener('keydown', (event) => {
    const step = {
      ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1,
    }[event.key];

    if (!step) return;

    const from = buttons.indexOf(document.activeElement);
    const to = from + step;

    if (from === -1 || to < 0 || to >= buttons.length) return;

    event.preventDefault();
    buttons[to].focus();
    onSelect(years[to]);
  });
}

// Shared by the legend and the tooltip, so the symbol standing for a category
// is the same object in both places. Every swatch carries the motif and only
// the mail-in one shows it, because the tooltip's swatch changes category as
// the pointer moves from state to state.
function buildSwatch(category) {
  const swatch = el('span', 'state-map__swatch');
  swatch.dataset.category = category;
  swatch.appendChild(buildEnvelope('state-map__swatch-motif'));

  return swatch;
}

// The share lines are filled in by setYear rather than here, so the legend
// doubles as the readout for whichever year the timeline is on.
function buildLegend() {
  const legend = el('div', 'state-map__legend');
  legend.appendChild(el('p', 'state-map__legend-title', 'Election voting options'));

  const items = el('ul', 'state-map__legend-items');
  const values = {};

  LEGEND_ORDER.forEach((key) => {
    const item = el('li', 'state-map__legend-item');
    const text = el('div', 'state-map__legend-text');

    values[key] = el('span', 'state-map__legend-value');

    text.appendChild(el('span', 'state-map__legend-label', LEGEND_LABELS[key]));
    text.appendChild(values[key]);

    item.appendChild(buildSwatch(key));
    item.appendChild(text);
    items.appendChild(item);
  });

  legend.appendChild(items);

  return { legend, values };
}

// State and the year being shown on one line, then the legend's own swatch and
// the category it stands for on the next.
function buildTooltip() {
  const tooltip = el('div', 'state-map__tooltip');
  tooltip.hidden = true;
  // Reachable by pointer only, and the map it sits over is aria-hidden too. The
  // table at the end carries all of this for anything that can't hover.
  tooltip.setAttribute('aria-hidden', 'true');

  const head = el('p', 'state-map__tooltip-head');
  const name = el('strong', 'state-map__tooltip-state');
  const year = el('span', 'state-map__tooltip-year');

  head.appendChild(name);
  head.appendChild(year);

  const category = el('p', 'state-map__tooltip-category');
  const swatch = buildSwatch('both');
  const label = el('span', 'state-map__tooltip-label');

  category.appendChild(swatch);
  category.appendChild(label);

  tooltip.appendChild(head);
  tooltip.appendChild(category);

  return {
    tooltip, name, year, swatch, label,
  };
}

function pointerAt(event, wrap) {
  const bounds = wrap.getBoundingClientRect();

  return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
}

// Offset off the pointer, then folded back inside whichever edge of the map it
// would otherwise cross. The map runs the full width of the graphic, so a card
// anchored on Maine or Florida has nowhere to go but back across the pointer;
// measuring first is what tells us which states those are at this width.
function placeTooltip(tooltip, wrap, point, above) {
  const cardWidth = tooltip.offsetWidth;
  const cardHeight = tooltip.offsetHeight;

  const right = point.x + TOOLTIP_OFFSET;
  const left = right + cardWidth > wrap.clientWidth - TOOLTIP_EDGE
    ? point.x - cardWidth - TOOLTIP_OFFSET
    : right;

  const under = point.y + TOOLTIP_OFFSET;
  const over = point.y - cardHeight - TOOLTIP_OFFSET;

  // A tap leaves a finger on the anchor, so those open upward, while a cursor
  // doesn't and opening downward keeps the pointer off the card. Either way the
  // other direction takes over once this one runs out of room.
  const fits = above ? over >= TOOLTIP_EDGE : under + cardHeight <= wrap.clientHeight - TOOLTIP_EDGE;
  const preferred = above ? over : under;
  const fallback = above ? under : over;

  tooltip.style.left = `${Math.round(Math.max(TOOLTIP_EDGE, left))}px`;
  tooltip.style.top = `${Math.round(Math.max(TOOLTIP_EDGE, fits ? preferred : fallback))}px`;
}

// A finger or a pen taps and has no hover to speak of; anything else, including
// a pointer that won't name itself, is treated as a cursor.
function taps(event) {
  return event.pointerType === 'touch' || event.pointerType === 'pen';
}

// Hover where there's a cursor, tap where there isn't. A tap leaves the card up
// until the next one, so it can still be read once the finger is gone.
//
// Returns a way to redraw the open card, for when the year changes underneath it.
function wireTooltip(root, wrap, tip, targets, render) {
  let active = null;

  const hide = () => {
    active = null;
    tip.tooltip.hidden = true;
  };

  const show = (index, event) => {
    active = index;
    render(index);
    // Unhidden before it's measured, or it measures zero
    tip.tooltip.hidden = false;
    placeTooltip(tip.tooltip, wrap, pointerAt(event, wrap), taps(event));
  };

  targets.forEach(({ node, index }) => {
    node.addEventListener('pointerenter', (event) => {
      if (taps(event)) return;
      show(index, event);
    });

    node.addEventListener('pointermove', (event) => {
      if (taps(event)) return;
      // Also reopens the card after a click, which the dismissal below closed
      if (active === index) placeTooltip(tip.tooltip, wrap, pointerAt(event, wrap), false);
      else show(index, event);
    });

    node.addEventListener('pointerleave', (event) => {
      if (taps(event)) return;
      hide();
    });

    node.addEventListener('pointerdown', (event) => {
      if (!taps(event)) return;
      // Keeps the dismissal below from closing the card in the same gesture
      event.stopPropagation();
      show(index, event);
    });
  });

  // A tap anywhere else in the graphic -- bare canvas, or the timeline -- puts
  // it away. Bound on the graphic's own root rather than the document so it
  // goes with the node on the next redraw instead of piling up listeners.
  //
  // Taps only: a cursor leaves on its own, and closing on mousedown would drop
  // the card on a click and not bring it back until the pointer left the state.
  root.addEventListener('pointerdown', (event) => {
    if (taps(event)) hide();
  });

  return () => {
    if (active !== null) render(active);
  };
}

// A square in the gutter beside New England, on a leader line back to the
// state's own position. Rhode Island, Connecticut and Delaware are too small at
// true scale to take a color off, and DC covers six pixels of the canvas.
function buildCallout(callout, labelSize) {
  const group = svgEl('g', 'state-map__callout');
  const half = CALLOUT_SIZE / 2;

  group.appendChild(attrs(svgEl('line', 'state-map__leader'), {
    x1: callout.cx,
    y1: callout.cy,
    x2: callout.x,
    y2: callout.y,
  }));

  const box = attrs(svgEl('rect', 'state-map__callout-box'), {
    x: callout.x,
    y: callout.y - half,
    width: CALLOUT_SIZE,
    height: CALLOUT_SIZE,
    rx: 2,
  });

  const motif = buildEnvelope('state-map__callout-motif', CALLOUT_SIZE);
  attrs(motif, { x: callout.x, y: callout.y - half });

  const label = attrs(svgEl('text', 'state-map__callout-label'), {
    x: callout.x + CALLOUT_SIZE + CALLOUT_LABEL_GAP,
    y: callout.y,
    dy: '0.32em',
    'font-size': labelSize,
  });
  label.textContent = callout.abbr;

  group.appendChild(box);
  group.appendChild(motif);
  group.appendChild(label);

  // The square and its label stand in for a shape too small to hover, so they
  // take the pointer for it. Two characters at the label's own size, which is
  // the widest of the four abbreviations plus a little slack.
  const hit = attrs(svgEl('rect', 'state-map__hit'), {
    x: callout.x,
    y: callout.y - half,
    width: round(CALLOUT_SIZE + CALLOUT_LABEL_GAP + (labelSize * 1.7)),
    height: CALLOUT_SIZE,
  });

  return {
    group, box, motif, hit,
  };
}

function buildMap(states, geo, unit, envelopePx) {
  const svg = attrs(svgEl('svg', 'state-map__map'), {
    viewBox: geo.viewBox,
    // The table below carries the same figures for screen readers
    'aria-hidden': 'true',
    focusable: 'false',
  });

  const defs = svgEl('defs');
  defs.appendChild(buildPattern(unit, envelopePx));
  svg.appendChild(defs);

  const fills = svgEl('g', 'state-map__fills');
  const motifs = svgEl('g', 'state-map__motifs');
  const borders = svgEl('g', 'state-map__borders');
  const callouts = svgEl('g', 'state-map__callouts');

  // Every outline a third time, invisible and on top, to catch the pointer. The
  // fill underneath can't do it: the motif and the borders are drawn over it,
  // and a hover would break wherever a state line or an envelope crossed.
  const hits = svgEl('g', 'state-map__hits');

  const shapes = states.map((state) => {
    const shape = geo.states[state.abbr];
    // Recolors run as a diagonal sweep out of the northwest instead of every
    // state flipping at once
    const delay = `${Math.round(((shape.cx / geo.mapWidth) + (shape.cy / geo.height)) * 63)}ms`;

    const fill = attrs(svgEl('path', 'state-map__state'), { d: shape.d });
    fill.style.setProperty('--shape-delay', delay);

    const motif = attrs(svgEl('path', 'state-map__motif'), { d: shape.d });
    motif.style.setProperty('--shape-delay', delay);

    // Borders go in their own layer rather than as a stroke on the fill, which
    // the tiled motif above it would paint over
    borders.appendChild(attrs(svgEl('path', 'state-map__border'), { d: shape.d }));

    const hit = attrs(svgEl('path', 'state-map__hit'), { d: shape.d });

    fills.appendChild(fill);
    motifs.appendChild(motif);
    hits.appendChild(hit);

    return { fill, motif, hit };
  });

  const labelSize = round(CALLOUT_LABEL_PX * unit);
  const byAbbr = {};

  geo.callouts.forEach((callout) => {
    const {
      group, box, motif, hit,
    } = buildCallout(callout, labelSize);

    callouts.appendChild(group);
    hits.appendChild(hit);
    byAbbr[callout.abbr] = { box, motif, hit };
  });

  svg.appendChild(fills);
  svg.appendChild(motifs);
  svg.appendChild(borders);
  svg.appendChild(callouts);
  svg.appendChild(hits);

  const targets = [];

  states.forEach((state, i) => {
    const callout = byAbbr[state.abbr];

    if (callout) shapes[i].callout = callout;

    targets.push({ node: shapes[i].hit, index: i });
    if (callout) targets.push({ node: callout.hit, index: i });
  });

  return { svg, shapes, targets };
}

// The map carries meaning through color and the envelope motif, so the figures
// go out again as a table for anything that can't see them.
function buildTable(states) {
  const table = el('table', 'state-map__table u-visually-hidden');
  const caption = el('caption');
  const head = el('thead');
  const headRow = el('tr');

  ['State', 'Voting options', 'Note'].forEach((text) => {
    const cell = el('th', null, text);
    cell.scope = 'col';
    headRow.appendChild(cell);
  });

  head.appendChild(headRow);

  const body = el('tbody');
  const rows = states.map((state) => {
    const row = el('tr');
    const name = el('th', null, state.name);
    name.scope = 'row';

    const value = el('td');
    const note = el('td');

    row.appendChild(name);
    row.appendChild(value);
    row.appendChild(note);
    body.appendChild(row);

    return { value, note };
  });

  table.appendChild(caption);
  table.appendChild(head);
  table.appendChild(body);

  return { table, caption, rows };
}

export default function graphic(props, container) {
  const { data, geo, width } = props;
  const { years, states } = data;
  const compact = width < COMPACT_BELOW;
  const size = metrics(width, compact);

  // Canvas units per screen pixel, for the few things that shouldn't scale
  const unit = geo.width / Math.max(width, 1);

  if (!years.includes(selectedYear)) selectedYear = years[years.length - 1];

  const countsByYear = years.map((year, i) => {
    const counts = { both: 0, earlyOnly: 0, neither: 0 };
    states.forEach((state) => { counts[state.values[i]] += 1; });

    return counts;
  });

  const root = el('div', 'state-map');
  root.dataset.compact = String(compact);
  root.dataset.legendStacked = String(width < LEGEND_STACKED_BELOW);
  root.style.setProperty('--tick-gap', `${size.tickGap}px`);
  root.style.setProperty('--tick-label-gap', `${size.tickLabelGap}px`);
  root.style.setProperty('--tick-label', `${TICK_LABEL_SIZE}px`);
  root.style.setProperty('--border-width', `${size.borderWidth}px`);
  root.style.setProperty('--bar-height', `${size.barHeight}px`);
  root.style.setProperty('--block-gap', `${size.blockGap}px`);

  const status = el('div', 'u-visually-hidden');
  status.setAttribute('role', 'status');

  const {
    timeline, ticks, buttons, hint,
  } = buildTimeline(years, countsByYear, compact);
  const { legend, values: shares } = buildLegend();
  const { svg, shapes, targets } = buildMap(states, geo, unit, size.envelope);
  const { table, caption, rows } = buildTable(states);

  // The tooltip is positioned against this, so it can only ever fold back
  // inside the map rather than the whole graphic
  const wrap = el('div', 'state-map__map-wrap');
  const tip = buildTooltip();
  wrap.appendChild(svg);
  wrap.appendChild(tip.tooltip);

  const renderTooltip = (index) => {
    const state = states[index];
    const category = state.values[years.indexOf(selectedYear)];

    tip.name.textContent = state.name;
    tip.year.textContent = `(${selectedYear})`;
    tip.swatch.setAttribute('data-category', category);
    tip.label.textContent = LEGEND_LABELS[category];
  };

  const refreshTooltip = wireTooltip(root, wrap, tip, targets, renderTooltip);

  const setYear = (year) => {
    selectedYear = year;

    const index = years.indexOf(year);

    status.textContent = `Showing ${year}`;
    caption.textContent = `Voting options by state, ${year}`;

    buttons.forEach((button, i) => {
      button.setAttribute('aria-pressed', String(i === index));
    });

    LEGEND_ORDER.forEach((key, i) => {
      const share = shareLabel(countsByYear[index][key], states.length);

      shares[key].textContent = i === 0 ? `${share}${SHARE_OF}` : share;
    });

    states.forEach((state, i) => {
      const category = state.values[index];
      const shape = shapes[i];

      shape.fill.setAttribute('data-category', category);
      shape.motif.setAttribute('data-category', category);

      if (shape.callout) {
        shape.callout.box.setAttribute('data-category', category);
        shape.callout.motif.setAttribute('data-category', category);
      }

      rows[i].value.textContent = LEGEND_LABELS[category];
      rows[i].note.textContent = noteText(state.notes[year] || '');
    });

    // Arrow keys can change the year with a card already open on a state
    refreshTooltip();
  };

  wireTimeline(ticks, buttons, years, (year) => {
    if (!tapHintUsed) {
      tapHintUsed = true;
      if (hint) hint.dataset.gone = 'true';
    }

    setYear(year);
  });

  // Outside the handler above, so opening on 2026 doesn't count as using it
  setYear(selectedYear);

  root.appendChild(legend);
  root.appendChild(timeline);
  root.appendChild(wrap);
  root.appendChild(status);
  root.appendChild(table);

  container.appendChild(root);
}
